import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as fragments from '~/apollo/fragments';
import type {
  StudySedimentaryLogsRouteQuery,
  StudySedimentaryLogsRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { useStudyOutletContext } from '~/routes/study/$studyId';

const STUDY_SEDIMENTARY_LOGS_ROUTE = gql`
  query StudySedimentaryLogsRoute($studyId: Int!) {
    studyList(id: $studyId) {
      ...studyParts
      sedimentaryLogs {
        ...sedimentaryLogParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
          }
        }
        files {
          ...fileParts
        }
        georeference {
          ...georeferenceParts
        }
      }
    }
  }

  ${fragments.studyParts}
  ${fragments.sedimentaryLogParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.georeferenceParts}
`;

export default function StudySedimentaryLogsRoute() {
  const ctx = useStudyOutletContext();
  const studyId = ctx.study.id;

  const { data, loading } = useQuery<
    StudySedimentaryLogsRouteQuery,
    StudySedimentaryLogsRouteQueryVariables
  >(STUDY_SEDIMENTARY_LOGS_ROUTE, { variables: { studyId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: STUDY_SEDIMENTARY_LOGS_ROUTE, variables: { studyId } },
  ];

  const sedLogs =
    data?.studyList.find(s => s.id === studyId)?.sedimentaryLogs ?? [];

  if (loading) return <SpinnerPlaceholder />;

  return (
    <SupportingObjectList
      items={sedLogs}
      soParentType="study"
      soParentId={studyId}
      bookmarkParentType={BookmarkParentType.SedimentaryLog}
      fade
      refetchQueries={refetchQueries}
    />
  );
}
